import React, { forwardRef, useRef, useImperativeHandle } from "react";
import { useGlobalValue } from "colbi_web_ui/lib/state/GlobalProvider";
import { SovosIcon } from "mosaic-react";
import { HighlightOff } from "mosaic-react-icons";
import { DatePicker, Switch, TextInput } from "colbi_web_ui/lib/components/inputs";
import { FORM_ACTIONS } from "../FormActions";
import styles from "./SeriesForm.module.sass";
import { adjustForTimezone } from "../../../utils/convertUtils";

const SeriesForm = (props, ref) => {
    const { data, status, onChange, onSave } = props;
    const { i18n, params } = useGlobalValue();

    const { projectId } = params;

    const formRef = useRef();
    const { userProjectSeries, include } = data || {};

    useImperativeHandle(ref, () => ({
        submit: () => {
            formRef.current.dispatchEvent(new Event("submit", { cancelable: true, bubbles: true }));
        },
    }));

    const handleChange = (e, prop, value) => {
        onChange &&
            onChange({
                originalEvent: e,
                target: {
                    value: {
                        data: {
                            ...data,
                            [prop]: value,
                        },
                    },
                },
            });
    };

    const tempObjectToCreateSeries = {
        Serie: "",
        FiscalYear: "",
        StartDate: null,
        EndDate: null,
    };

    function validateDate(paramDate) {
        if (!paramDate) {
            return null;
        }
        return paramDate ? adjustForTimezone(paramDate) : null;
    }

    const organizationsItems = (userProjectSeries || []).map((projectSerie, index) => {
        const { Serie, FiscalYear, StartDate, EndDate } = projectSerie || {};
        return (
            <div key={index} className={styles["container"]}>
                <div className={styles["group-content"]}>
                    <div className={styles["flex"]}>
                        <div>
                            <TextInput
                                className={styles["login-text-input"]}
                                type="text"
                                autoComplete="off"
                                autoCorrect="off"
                                autoCapitalize="off"
                                spellCheck="false"
                                name={i18n`Serie`}
                                placeholder={i18n`Enter a Serie`}
                                required
                                value={Serie || ""}
                                onChange={(e) => {
                                    const userSerie = userProjectSeries[index];
                                    const nextUserSerie = [...(userProjectSeries || [])];
                                    nextUserSerie.splice(index, 1, {
                                        ...userSerie,
                                        Serie: e.target.value || "",
                                    });
                                    handleChange(e, `userProjectSeries`, nextUserSerie);
                                }}
                            />
                        </div>
                        <div>
                            <TextInput
                                className={styles["login-text-input"]}
                                type="text"
                                autoComplete="off"
                                autoCorrect="off"
                                maxLength="4"
                                autoCapitalize="off"
                                spellCheck="false"
                                name={i18n`Fiscal Year`}
                                placeholder={i18n`Enter a Fiscal Year`}
                                value={FiscalYear || ""}
                                onChange={(e) => {
                                    const userSerie = userProjectSeries[index];
                                    const nextUserSerie = [...(userProjectSeries || [])];
                                    nextUserSerie.splice(index, 1, {
                                        ...userSerie,
                                        FiscalYear: e.target.value || "",
                                    });
                                    handleChange(e, `userProjectSeries`, nextUserSerie);
                                }}
                            />
                        </div>
                    </div>
                </div>
                <div className={styles["group-content"]}>
                    <div className={styles["flex"]}>
                        <DatePicker
                            className={styles["wrap-datepicker"]}
                            name={i18n`Start Date`}
                            autoComplete="off"
                            value={validateDate(StartDate)}
                            placeholderText=""
                            isClearable
                            onChange={(e) => {
                                const userSerie = userProjectSeries[index];
                                const nextUserSerie = [...(userProjectSeries || [])];
                                nextUserSerie.splice(index, 1, {
                                    ...userSerie,
                                    StartDate: e || null,
                                });
                                handleChange(e, `userProjectSeries`, nextUserSerie);
                            }}
                            dateFormat="yyyy-MM-dd"
                        />
                        <DatePicker
                            className={styles["wrap-datepicker"]}
                            name={i18n`End Date`}
                            autoComplete="off"
                            value={validateDate(EndDate)}
                            placeholderText=""
                            isClearable
                            onChange={(e) => {
                                const userSerie = userProjectSeries[index];
                                const nextUserSerie = [...(userProjectSeries || [])];
                                nextUserSerie.splice(index, 1, {
                                    ...userSerie,
                                    EndDate: e || null,
                                });
                                handleChange(e, `userProjectSeries`, nextUserSerie);
                            }}
                            dateFormat="yyyy-MM-dd"
                        />
                    </div>
                </div>
                <div className={styles["remove-button-container"]}>
                    <button
                        type="button"
                        className={styles["remove-button"]}
                        onClick={(e) => {
                            const nextOrganization = [...(userProjectSeries || [])];
                            nextOrganization.splice(index, 1);
                            handleChange(e, "userProjectSeries", nextOrganization);
                        }}
                    >
                        <SovosIcon>
                            <HighlightOff />
                        </SovosIcon>
                    </button>
                </div>
            </div>
        );
    });

    return status !== "loading" ? (
        <form
            ref={formRef}
            onSubmit={(e) => {
                e.stopPropagation();
                e.preventDefault();
                if (formRef.current.checkValidity()) {
                    data["__typename"] = "ProjectSeries";
                    data["projectId"] = projectId;
                    data["include"] = include;
                    onSave && onSave();
                } else {
                    formRef.current.reportValidity();
                }
            }}
        >
            <div className={styles["input-field"]}>
                <Switch
                    name={i18n`Include Series?`}
                    checked={include || false}
                    onChange={(e) => {
                        handleChange(e, "include", e.target.value);
                    }}
                />
                <h5 className={styles["label"]}>{i18n`Series`}</h5>
                <div>
                    {organizationsItems.length ? (
                        organizationsItems
                    ) : (
                        <small>{i18n`No series assigned`}</small>
                    )}
                </div>
                <div>
                    <button
                        className={styles["add-button"]}
                        type="button"
                        onClick={(e) => {
                            handleChange(e, "userProjectSeries", [
                                ...(userProjectSeries || []),
                                tempObjectToCreateSeries,
                            ]);
                        }}
                    >
                        <small>{i18n`[+] Add new Serie`}</small>
                    </button>
                </div>
            </div>
        </form>
    ) : null;
};

const SeriesFormRef = forwardRef(SeriesForm);

SeriesFormRef.queries = (args) => {
    return [
        {
            resource: "project_series",
            args: {
                projectId: args.projectId,
            },
            body: "userProjectSeries{ID, ProjectID, FiscalYear, Serie, StartDate, EndDate}, include",
        },
    ].filter((q) => q);
};

SeriesFormRef.dataMapper = ["data"];

SeriesFormRef.actions = [FORM_ACTIONS.SAVE];

export default SeriesFormRef;
